import { useMemo, useState } from "react";
import moment from "moment/moment";
// @ts-expect-error pure js
import useDebouncedValue from "./use-debounced-value";
import type { Balance } from "../models/balance";

interface UseBalanceFiltersOptions {
  balances: Balance[];
  getBalanceLabel: (balance: Balance) => string;
  getTypeLabel: (type: string) => string;
}

function includesIgnoreCase(text: string | undefined, needle: string) {
  return !!text && text.toLowerCase().includes(needle);
}

function matchesSearch(
  balance: Balance,
  needle: string,
  getBalanceLabel: (balance: Balance) => string,
) {
  if (
    includesIgnoreCase(getBalanceLabel(balance), needle) ||
    includesIgnoreCase(balance.description, needle) ||
    includesIgnoreCase(balance.comment, needle)
  ) {
    return true;
  }
  return (balance.balances ?? []).some(
    (child) =>
      includesIgnoreCase(child.description, needle) ||
      includesIgnoreCase(child.comment, needle),
  );
}

export default function useBalanceFilters({
  balances,
  getBalanceLabel,
  getTypeLabel,
}: UseBalanceFiltersOptions) {
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [montantMin, setMontantMin] = useState("");
  const [montantMax, setMontantMax] = useState("");
  const [dateMin, setDateMin] = useState("");
  const [dateMax, setDateMax] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const debouncedMontantMin = useDebouncedValue(montantMin);
  const debouncedMontantMax = useDebouncedValue(montantMax);

  const typeOptions = useMemo(() => {
    const types = [...new Set(balances.map((balance) => balance.type))];
    return types
      .map((type) => ({ value: type, label: getTypeLabel(type) || type }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [balances, getTypeLabel]);

  const filteredBalances = useMemo(() => {
    const needle = debouncedSearch.trim().toLowerCase();
    const min = Number(debouncedMontantMin);
    const max = Number(debouncedMontantMax);
    const hasMin = debouncedMontantMin !== "" && !Number.isNaN(min);
    const hasMax = debouncedMontantMax !== "" && !Number.isNaN(max);

    return balances.filter((balance) => {
      if (typeFilter.length > 0 && !typeFilter.includes(balance.type)) {
        return false;
      }
      // Les montants sont comparés en valeur absolue : les appels (débits)
      // sont stockés en négatif mais l'utilisateur saisit un montant positif.
      const montant = Math.abs(balance.montant);
      if (hasMin && montant < min) return false;
      if (hasMax && montant > max) return false;
      if (dateMin && moment(balance.date_transac).isBefore(dateMin, "day")) {
        return false;
      }
      if (dateMax && moment(balance.date_transac).isAfter(dateMax, "day")) {
        return false;
      }
      if (needle && !matchesSearch(balance, needle, getBalanceLabel)) {
        return false;
      }
      return true;
    });
  }, [
    balances,
    getBalanceLabel,
    typeFilter,
    debouncedSearch,
    debouncedMontantMin,
    debouncedMontantMax,
    dateMin,
    dateMax,
  ]);

  const activeFiltersCount =
    (typeFilter.length > 0 ? 1 : 0) +
    (montantMin || montantMax ? 1 : 0) +
    (dateMin || dateMax ? 1 : 0);

  function clearFilters() {
    setTypeFilter([]);
    setMontantMin("");
    setMontantMax("");
    setDateMin("");
    setDateMax("");
  }

  return {
    filteredBalances,
    typeOptions,
    search,
    setSearch,
    typeFilter,
    setTypeFilter,
    montantMin,
    setMontantMin,
    montantMax,
    setMontantMax,
    dateMin,
    setDateMin,
    dateMax,
    setDateMax,
    activeFiltersCount,
    clearFilters,
  };
}

export type BalanceFilters = ReturnType<typeof useBalanceFilters>;
